import { Provider } from '@weave/schema-gen-ts/dist/shared/porting/v1/enums.pb';
import { UsageType } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/phone_number.pb';
import { purchasePhoneNumber } from '../../../apis/phone-numbers/phone-numbers.api';
import { CustomAxios } from '../../../redux/axios';

export interface NumberToPurchase {
  phoneNumber: string;
  provider?: Provider;
}

export interface PurchasedNumberResult {
  requestedPhoneNumber: string;
  phoneNumberId?: string;
}

export interface PurchaseFailure {
  requestedPhoneNumber: string;
  error: unknown;
}

interface PurchaseNumbersArgs {
  numbersToPurchase: NumberToPurchase[];
  assignToDefaultCallRoute?: boolean;
  faxOnly?: boolean;
}

export const purchaseNumbers = async ({
  numbersToPurchase,
  assignToDefaultCallRoute,
  faxOnly,
}: PurchaseNumbersArgs): Promise<{
  purchasedNumbers: PurchasedNumberResult[];
  failures: PurchaseFailure[];
}> => {
  const settledPurchases = await Promise.allSettled(
    numbersToPurchase.map(async (num) => {
      const response = await purchasePhoneNumber({
        phoneNumber: num.phoneNumber,
        usageType: UsageType.USAGE_TYPE_MAIN,
        provider: num.provider ?? Provider.PROVIDER_UNSPECIFIED,
        assignToDefaultCallRoute,
        faxOnly,
      });

      return {
        requestedPhoneNumber: num.phoneNumber,
        phoneNumberId: response?.number?.phoneNumber?.id,
      } as PurchasedNumberResult;
    })
  );

  const purchasedNumbers: PurchasedNumberResult[] = [];
  const failures: PurchaseFailure[] = [];

  settledPurchases.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      purchasedNumbers.push(result.value);
      return;
    }

    failures.push({
      requestedPhoneNumber: numbersToPurchase[index].phoneNumber,
      error: result.reason,
    });
  });

  return { purchasedNumbers, failures };
};

export const enableOutboundCallerIdForPurchasedNumbers = async (
  purchasedNumbers: PurchasedNumberResult[]
): Promise<PurchaseFailure[]> => {
  const purchasedNumbersWithIds = purchasedNumbers.filter(
    (purchasedNumber) => !!purchasedNumber.phoneNumberId
  );

  const settledUpdates = await Promise.allSettled(
    purchasedNumbersWithIds.map(async (purchasedNumber) => {
      await CustomAxios.put(
        `phone-exp/phone-numbers/${purchasedNumber.phoneNumberId}/outbound-caller-id`,
        {
          phoneNumberId: purchasedNumber.phoneNumberId,
          enabled: true,
        }
      );

      return purchasedNumber.requestedPhoneNumber;
    })
  );

  const failures: PurchaseFailure[] = [];

  settledUpdates.forEach((result, index) => {
    if (result.status === 'rejected') {
      failures.push({
        requestedPhoneNumber: purchasedNumbersWithIds[index].requestedPhoneNumber,
        error: result.reason,
      });
    }
  });

  return failures;
};
